/**
 * GridCanvas — reusable pan/zoom SVG "graph paper" canvas with an optional Excel-freeze-pane-style
 * frozen row/column. This is the generic engine TimelineCanvas.tsx (the Plan page's Timeline
 * panel) is built on; it knows nothing about PlanBoardItem/PlanCardPlacement/PlanBoardCard or
 * any other specific kind of content — every placed item is rendered by the caller's own
 * `renderItem` callback, keyed by plain `{x,y,width,height}` placements. That's what makes it
 * safe to mount more than once in the app, each with entirely independent geometry, colors, and
 * frozen-pane configuration (every constant TimelineCanvas used to hardcode is a prop here, with
 * defaults chosen to be sensible for a fresh instance rather than to match Timeline's specific
 * look) — see TimelineCanvas.tsx for a fully worked example of specializing it for one kind of
 * content.
 *
 * Zoom starts at the fit-to-width level computed on mount — or, if a caller passes `initialZoom`
 * (e.g. restoring a project's last-used zoom, see TimelineCanvas.tsx/PlanPage.tsx), that value
 * instead — and the user can zoom further via wheel/trackpad scroll, centered on the cursor (the
 * canvas point under the pointer stays fixed on screen as zoom changes), bounded by the `minZoom`/
 * `maxZoom` props. Every zoom change (including the initial fit/restore and every step of the
 * pan/zoom lerp) is reported to `onZoomChange`, if passed, so a caller can persist it. Panning
 * (both axes) is a separate gesture — click and drag the background. Both pan and zoom are
 * lerped toward their target each frame (rather than snapping instantly) for a smooth, damped
 * feel. The grid has a fixed horizontal extent of `horizontalColumns` major cells and a fixed
 * vertical extent of `verticalExtent` canvas units; on mount, zoom is set so `visibleColumns` of
 * those fit the panel exactly, with any remainder reachable by panning right. Panning is clamped
 * on EVERY edge — left, right, top, and bottom — so the user can never scroll past any of the
 * grid's four fixed boundaries, at any zoom level.
 *
 * **Lines stay a constant on-screen thickness regardless of zoom.** `lineThickness` and
 * `dividerWidth`-adjacent measurements that live inside the zoom-scaled body/overlay panes (the
 * drawn `lines`, and the frozen-gap segment divider bars that mirror them — see
 * `renderFrozenRowSegment`/`renderFrozenColumnSegment` below) are actually rendered at
 * `lineThickness / zoom` canvas units, not `lineThickness` itself — since everything in those
 * panes is then scaled by `zoom`, dividing first cancels that scale out, so the prop's value is
 * what actually reaches the screen in CSS px, unchanged as zoom changes. (The frozen-boundary
 * dividers and everything else outside those scaled panes render in plain, unscaled screen space
 * already — no such compensation needed there.)
 *
 * **Frozen row/column** (optional — see `frozenColumnGapPx`/`frozenRowGapPx`, each 0 by default
 * i.e. disabled): the first `frozenColumnGapPx`/`frozenRowGapPx` CSS px of columns/rows are
 * pinned to the viewport's left/top edges, Excel-freeze-pane style, unaffected by panning in that
 * axis while still panning along the other axis with the rest of the grid. Either axis can be
 * enabled independently. Unlike canvas geometry, this size is a fixed on-screen pixel count, not
 * scaled by zoom — and it never changes after mount: there is no expand/collapse affordance:
 * the reserved gap is always exactly this size. (A generic `renderFrozenRowSegment`/
 * `renderFrozenColumnSegment` callback pair lets a caller render its own content — e.g.
 * TimelineCanvas's per-axis name-label cells — inside the segments that gap is cut into by the
 * SAME lines that cut the body into regions; see those props' own doc comments below.)
 */
import { useRef, useState, useEffect, useLayoutEffect, useImperativeHandle, forwardRef, ReactNode, MouseEvent, CSSProperties } from 'react';
import GridLines, { GridPatternVariant } from './GridLines';
import { screenToCanvasPoint } from 'utils/panZoom';
import { buildRectConnectorPath } from 'utils/canvasUtils';
import { PortSide } from 'utils/nodePorts';

/** A placed item's position/size, in canvas units. Structurally what every placements record
 * entry must satisfy — e.g. PlanCardPlacement (types.ts) already matches this shape exactly. */
export type CanvasRect = { x: number; y: number; width: number; height: number };

/** A straight vertical/horizontal line spanning the pannable body from its near edge (left/top)
 * to effectively infinite on its far edge (right/bottom — see INFINITE_EXTENT_CANVAS_UNITS) — e.g.
 * PlanDrawnLine (types.ts) already matches this shape. Rendered directly inside the body's own
 * clipped/transformed SVG group (see the `lines` prop below), so it's automatically confined to
 * the scrollable body exactly like the grid lines it's drawn alongside — it can never spill into
 * a frozen row/column, regardless of where `position` falls, without any extra bucketing logic.
 * Also what cuts the frozen row/column's own gap into labelable segments — see
 * `renderFrozenRowSegment`/`renderFrozenColumnSegment` below. */
export type DrawnLine = {
    id: string;
    orientation: 'vertical' | 'horizontal';
    position: number;
    color: string;
    /** Optional display name. When set, rendered as a small label rectangle (filled with the
     * line's own color) joined to the line — above-and-left for a horizontal line, left for a
     * vertical one — pinned to the pannable body's near edge on the line's OWN axis so it stays
     * on screen regardless of how far the line is panned along its length (see the `lines`
     * label rendering below), while still tracking pan/zoom on the perpendicular axis so it
     * stays attached to the line's actual position. */
    name?: string;
};

/** Screen-space (viewport/fixed) rectangle — what canvasRectToScreenRect resolves a CanvasRect to. */
export type ScreenRect = { left: number; top: number; width: number; height: number };

/**
 * A routed connector between two placed items, identified by their `placements` ids — the
 * generic, id-referencing counterpart to `DrawnLine` above (GridCanvas resolves `fromId`/`toId`
 * against its own `placements` prop, the same way it already does for `renderItem`, so it never
 * needs to know what an item's content actually is). Routed via buildRectConnectorPath
 * (utils/canvasUtils.tsx) — the same rounded-elbow port-selection logic OST's tree edges use —
 * and rendered with an arrowhead at the target end and a filled circle at the origin (see the
 * `connectors` rendering below), distinct from both OST's plain tree edges (no arrowhead/circle)
 * and its dashed relation overlay (circles at both ends, no arrowhead).
 */
export type CanvasConnector = {
    id: string;
    fromId: string;
    toId: string;
    color: string;
};

/** The live preview of a connector still being drawn (e.g. mid Ctrl+drag) — already-resolved
 * canvas-space endpoints rather than placement ids, since the drag's origin/current point isn't
 * necessarily anchored to either placement's own port geometry yet. */
export type CanvasConnectorPreview = {
    from: { x: number; y: number };
    to: { x: number; y: number };
    color: string;
};

export interface GridCanvasHandle {
    /** Converts a screen point (e.g. e.clientX/clientY) to canvas space using the current pan/zoom. */
    screenToCanvas(clientX: number, clientY: number): { x: number; y: number } | null;
    /** Converts a canvas-space rect to a viewport-fixed screen rect using the current pan/zoom. */
    canvasRectToScreenRect(rect: CanvasRect): ScreenRect | null;
    /** The canvas wrapper's current bounding rect, for hit-testing whether a drag is over the canvas. */
    getWrapperRect(): DOMRect | null;
    /** Snaps a canvas-space point to the minor grid (gridSpacing / minorDivisions). */
    snapPoint(x: number, y: number): { x: number; y: number };
    /** Snaps a size to the minor grid, clamped to minSnapWidth/minSnapHeight. */
    snapSize(width: number, height: number): { width: number; height: number };
    /** The current zoom level, for converting screen-space drag deltas to canvas units. */
    getZoom(): number;
    /** The frozen column's reserved width, converted from its fixed on-screen `frozenColumnGapPx`
     * into canvas units at the current zoom. 0 if the frozen column is disabled
     * (`frozenColumnGapPx` prop is 0). Useful for a caller that, like PlanPage's
     * resolveFrozenAreaCandidate, needs its own drop-rejection boundary in canvas space. */
    getFrozenColumnWidth(): number;
    /** Same as getFrozenColumnWidth, for the frozen row's height. */
    getFrozenRowHeight(): number;
}

export interface GridCanvasProps {
    /** Current placements (canvas units) for every item to render, keyed by an id meaningful to the caller. */
    placements: Record<string, CanvasRect>;
    /** Renders one placed item — return null to render nothing for that id (e.g. a hidden/ghosted
     * item mid-drag). Fully owns lookup of whatever content data the id maps to; GridCanvas
     * itself has no concept of "item content", only placement geometry. */
    renderItem: (id: string, placement: CanvasRect) => ReactNode;
    /** Called when the grid's empty background (not an item) is clicked — as opposed to
     * dragged/panned. Receives the native mouseup event so a caller that needs the click's
     * canvas-space location (e.g. placing a drawn line) can resolve it via screenToCanvas. */
    onBackgroundClick?: (e: globalThis.MouseEvent) => void;
    /** Swaps the cursor to `not-allowed` while true — e.g. for drag-and-drop rejection feedback. */
    dropForbidden?: boolean;
    /** Swaps the cursor to `crosshair` while true — e.g. while a "click to place" tool (like a
     * drawn line) is armed and waiting for the user to pick a spot. Takes precedence over
     * dropForbidden's cursor when both are somehow true. */
    crosshair?: boolean;
    /** Straight vertical/horizontal lines to render, edge to edge across the pannable body —
     * see the DrawnLine type above. Drawn inside the same clipped/transformed SVG group as the
     * grid lines, after them, so they always sit visually on top of the grid but are still
     * confined to the scrollable body. Also what cuts the frozen row/column's gap into segments
     * (see renderFrozenRowSegment/renderFrozenColumnSegment). */
    lines?: DrawnLine[];
    /** Stroke width, in CONSTANT on-screen CSS px, for every entry in `lines` (and the matching
     * frozen-gap segment divider bars — see renderFrozenRowSegment/renderFrozenColumnSegment)
     * — internally divided by the current zoom before being used as a canvas-space size, so the
     * rendered thickness never visually grows/shrinks as the user zooms. Default 4. */
    lineThickness?: number;
    /** Id of the line (from `lines`) currently selected — rendered with a highlight halo. */
    selectedLineId?: string | null;
    /** Fired when a rendered line is pressed — e.g. for the caller to select it. Stops the event
     * from also being treated as a background pan/click. */
    onLineMouseDown?: (lineId: string, e: MouseEvent<SVGLineElement>) => void;
    /** Routed connectors between placed items — see CanvasConnector above. Rendered in the same
     * clipped/transformed body group as `lines`, after them, so connectors sit on top of both the
     * grid and any drawn lines but stay confined to the scrollable body like everything else there. */
    connectors?: CanvasConnector[];
    /** The live preview of a connector still being drawn — see CanvasConnectorPreview above. */
    connectorPreview?: CanvasConnectorPreview | null;
    /** Id of the connector (from `connectors`) currently selected — rendered slightly thicker/
     * larger than normal, in its own `color` (see CONNECTOR_SELECTED_STROKE_SCALE and friends),
     * mirroring `selectedLineId`. */
    selectedConnectorId?: string | null;
    /** Fired when a rendered connector is pressed — e.g. for the caller to select it (and, on a
     * subsequent Delete/Backspace, remove it). Stops the event from also being treated as a
     * background pan/click, same as onLineMouseDown. */
    onConnectorMouseDown?: (connectorId: string, e: MouseEvent<SVGPathElement>) => void;
    /**
     * Renders the content of one segment of the frozen ROW's gap — the row is cut along x by
     * every VERTICAL line's position, into `verticalLines.length + 1` segments left to right
     * (zero vertical lines still gives one segment spanning the row's full width), the SAME cut
     * points that divide the body into region COLUMNS (see PlanRegion, types.ts, and
     * TimelineCanvas's own `columnIndex`). `index` is that same 0-based left-to-right ordinal;
     * `xMin`/`xMax` are the segment's canvas-space x bounds. `visibleMin`/`visibleMax` are the
     * portion of `[xMin, xMax]` currently on screen (clamped to the segment's own bounds — equal
     * to `xMin`/`xMax` themselves whenever the whole segment fits in the viewport) at the CURRENT
     * pan/zoom, letting a caller keep its own label content centered in what's actually visible
     * rather than in the segment's full extent, which can run off screen while zoomed in. Only
     * called when the frozen row is enabled (`frozenRowGapPx > 0`); no-op (nothing rendered) when
     * omitted.
     */
    renderFrozenRowSegment?: (index: number, xMin: number, xMax: number, visibleMin: number, visibleMax: number) => ReactNode;
    /**
     * Renders the content of one segment of the frozen COLUMN's gap — cut along y by every
     * HORIZONTAL line's position, into `horizontalLines.length + 1` segments top to bottom,
     * the same cut points that divide the body into region ROWS. `index` is that 0-based
     * top-to-bottom ordinal; `yMin`/`yMax` are the segment's canvas-space y bounds;
     * `visibleMin`/`visibleMax` are the visible portion of `[yMin, yMax]`, same idea as
     * renderFrozenRowSegment's own visibleMin/visibleMax. Only called when the frozen column is
     * enabled (`frozenColumnGapPx > 0`).
     */
    renderFrozenColumnSegment?: (index: number, yMin: number, yMax: number, visibleMin: number, visibleMax: number) => ReactNode;
    /** Floating overlay (e.g. a corner menu) rendered on top of the canvas, inside its wrapper —
     * doesn't participate in pan/zoom/grid geometry at all. */
    overlay?: ReactNode;
    /** Extra class suffix applied alongside every `grid-canvas-*` structural class (e.g. passing
     * 'timeline-canvas' renders `class="grid-canvas-wrapper timeline-canvas-wrapper"` etc.) — the
     * per-instance hook a page-specific stylesheet or test can target without touching the shared
     * structural rules every GridCanvas instance relies on. Defaults to 'grid-canvas' (no second class). */
    classPrefix?: string;

    // ── Grid geometry (canvas units unless noted) ──────────────────────────────────────────
    /** Side length of one major grid cell. Default 40. */
    gridSpacing?: number;
    /** Number of minor divisions per major cell — also the snapping unit's divisor. Default 5. */
    minorDivisions?: number;
    /** Number of major columns the grid spans horizontally (its fixed, pannable width). Default 16. */
    horizontalColumns?: number;
    /** Number of major columns shown at the mount-time fit-to-width zoom; the remainder extends
     * past the viewport, reachable by panning right. Defaults to horizontalColumns - 1 (kept
     * below horizontalColumns so the extra column actually widens the pannable canvas instead of
     * just shrinking every column to still fit the viewport exactly). */
    visibleColumns?: number;
    /** How far down grid lines are rendered — the bottom stays open/unbounded in practice as long
     * as this comfortably exceeds anything the user will ever pan to. Default 20000
     * (DEFAULT_GRID_VERTICAL_EXTENT). */
    verticalExtent?: number;
    /** Smallest width/height snapSize will ever return. Defaults to one minor cell. */
    minSnapWidth?: number;
    minSnapHeight?: number;

    // ── Frozen row/column ("Excel freeze pane") — each independently optional ──────────────
    /** Fixed on-screen width (CSS px, NOT canvas units — never scaled by zoom) of the frozen
     * column's reserved gap, pinned to the viewport's left edge. 0 (default) disables it
     * entirely. Permanent: there is no expand/collapse — this is always the pane's exact size. */
    frozenColumnGapPx?: number;
    /** Same as frozenColumnGapPx, for the frozen row's height. */
    frozenRowGapPx?: number;

    // ── Appearance ──────────────────────────────────────────────────────────────────────────
    /** Canvas background color. Default a "blueprint" blue (#5f84b8). */
    backgroundColor?: string;
    /** Grid pattern: cross-hatch lines (default, rendered as two layers — minor + major, see
     * gridSpacing/minorDivisions) or a single flat layer of dots (one size/spacing, no
     * minor/major distinction — matching the OST canvas's own dot grid). Callers pick this per
     * instance/theme rather than GridCanvas favoring either — see GridLines.tsx. */
    gridStyle?: GridPatternVariant;
    /** Grid pattern color (lines or dots, depending on gridStyle). Default white. */
    gridLineColor?: string;
    /** Opacity of the grid pattern layer(s). Default 0.18. */
    gridLineOpacity?: number;
    /** Dot radius (canvas units), used only when gridStyle='dots'. Default 1.5. */
    gridDotRadius?: number;
    /** Spacing between dots (canvas units), used only when gridStyle='dots' — independent of
     * gridSpacing/minorDivisions (those remain the snapping/column geometry regardless of
     * gridStyle). Defaults to gridSpacing. */
    gridDotSpacing?: number;
    /** Frozen-boundary divider line color. Default white. */
    dividerColor?: string;
    /** Frozen-boundary divider line width (CSS px). Default 2. */
    dividerWidth?: number;

    // ── Zoom ────────────────────────────────────────────────────────────────────────────────
    /** Absolute zoom floor — also bounds the mount-time fit-to-width computation (and
     * `initialZoom`, below, if given one outside this range). The user can wheel-zoom out this
     * far, past the fit-to-width view. Default 0.3. */
    minZoom?: number;
    /** Absolute zoom ceiling — how far in the user can wheel-zoom, and also bounds the
     * fit-to-width computation. Default 3. */
    maxZoom?: number;
    /** Zoom to start at on mount instead of computing the fit-to-width level — e.g. restoring a
     * project's last-used zoom (TimelineCanvas.tsx/PlanPage.tsx). Clamped to [minZoom, maxZoom]
     * like any other zoom value. Omit to use the computed fit-to-width zoom, same as before this
     * prop existed. */
    initialZoom?: number;
    /** Called with the current zoom every time it changes — the initial fit/restore, and every
     * step of the pan/zoom lerp while wheel-zooming (not just once it settles) — so a caller that
     * wants to persist it (e.g. PlanPage.tsx, debounced) always has the latest value. */
    onZoomChange?: (zoom: number) => void;
}

/** Default value of the `verticalExtent` prop — how far down grid lines are rendered, with the
 * bottom staying open/unbounded in practice as long as this comfortably exceeds anything the user
 * will ever pan to. Exported (not just inlined as the prop default below) so a caller that needs
 * to reason about the grid's actual bounds without overriding this prop itself — e.g.
 * TimelineCanvas computing Plan region boundaries (see utils/planRegions.ts) — uses the exact
 * same value GridCanvas renders with, rather than a second hardcoded copy that could drift. */
export const DEFAULT_GRID_VERTICAL_EXTENT = 20000;

/** Pointer movement (px) below which a mousedown/mouseup pair on the grid background counts as a click rather than a pan. */
const BACKGROUND_CLICK_THRESHOLD_PX = 4;
/** Fraction of the remaining distance to target covered per animation frame, for the zoom/pan lerp. */
const LERP_FACTOR = 0.22;
/** Exponential rate the wheel/trackpad zoom multiplies the current zoom by per unit of
 * `deltaY` — `nextZoom = currentZoom * exp(-deltaY * WHEEL_ZOOM_SENSITIVITY)`. Chosen so a
 * typical mouse-wheel notch (~100 deltaY) changes zoom by roughly 15%, a comfortable per-click
 * step; a trackpad's much smaller continuous deltas scale down proportionally, reading as smooth. */
const WHEEL_ZOOM_SENSITIVITY = 0.0016;
/** Once current is within this fraction/unit of target, snap and stop animating. */
const SNAP_EPSILON = 0.001;
/** Minimum screen-px stroke width for a drawn line's invisible hit-zone (see the `lines`
 * rendering below) — `lineThickness` itself is now always this many px on screen regardless of
 * zoom (see lineThicknessCanvas), but a caller could still pass a `lineThickness` prop smaller
 * than this, so clicking/dragging it would otherwise require pixel-precision. */
const DRAWN_LINE_HIT_ZONE_MIN_PX = 12;
/** Screen-px gap between a drawn line's near frozen-pane edge and its label rectangle (see the
 * `lines` label rendering below) — keeps the label from touching the frozen divider. */
const DRAWN_LINE_LABEL_EDGE_GAP_PX = 8;
/** Canvas-unit length a user-drawn line's right/bottom end — and, identically, the grid lines'
 * own right/bottom bound (see renderGridLines) — is extended to, far past `gridWidth`/
 * `verticalExtent`. So a horizontal line/grid row always keeps going off the right edge, and a
 * vertical line/grid column off the bottom edge, reading as truly infinite rather than visibly
 * stopping once the user zooms out enough to see the grid's actual (finite) bounds. Large enough
 * that even at the lowest allowed zoom (`minZoom`, floored at 0.3 by default) the on-screen length
 * still comfortably exceeds any real viewport — the SVG body's own clipPath (see `scrollableClipId`
 * above) is what actually truncates it at the visible edge, not this length running out. */
const INFINITE_EXTENT_CANVAS_UNITS = 1_000_000;
/** Zoom level below which the minor (fine, lighter-gray) grid line layer is hidden — only the
 * major layer keeps rendering. Zoomed out this far, the minor cells are so small on screen they
 * read as visual noise rather than a usable snapping guide, so they fade out of the way; zooming
 * back in past this threshold brings them straight back. The major layer is never hidden this way. */
const MINOR_GRID_HIDE_ZOOM_THRESHOLD = 0.5;
/** Constant on-screen (CSS px) stroke width of a connector's own line — deliberately its own
 * constant rather than reusing `lineThickness` (the user-drawn-line prop; Plan's Timeline canvas
 * passes TIMELINE_DRAWN_LINE_THICKNESS = 2px for that), so a connector reads as a slightly finer,
 * more "arrow-like" stroke than a drawn line. Same zoom-compensation idea as `lineThicknessCanvas`
 * (see connectorLineThicknessCanvas). */
const CONNECTOR_LINE_THICKNESS_PX = 1.5;
/** Constant on-screen (CSS px) length of a connector's arrowhead marker — like `lineThickness`,
 * divided by zoom before use (see connectorArrowSizeCanvas) so it never visually grows/shrinks
 * as the user zooms. */
const CONNECTOR_ARROW_SIZE_PX = 7;
/** Constant on-screen (CSS px) radius of a connector's origin dot — same zoom-compensation idea. */
const CONNECTOR_ORIGIN_DOT_RADIUS_PX = 3;
/** Port gap (canvas units) a connector's own start/end points sit away from each item's edge —
 * 0, unlike buildEdgePath's OST default of 6, so the line/arrowhead/origin-dot land directly on
 * the item's border instead of floating off it. */
const CONNECTOR_PORT_GAP = 0;
/** Unlike buildEdgePath's OST default (a rectilinear elbow, rounded corners falling back to sharp
 * ones only when the two nodes are too close together to fit a curve), a Plan card connector
 * always routes as a single smooth bezier curve (see buildCurvedPortPath) for a hand-drafted,
 * flowchart-style look — passed as buildRectConnectorPath's `curved` param, which makes the
 * `rounded` param moot (it's only consulted for the elbow routing curved bypasses). */
const CONNECTOR_CURVED = true;
/** Routes Plan card connectors via selectDominantAxisPorts (buildRectConnectorPath's `axisBias`)
 * instead of buildEdgePath's OST zone system — the two cards a connector joins have no
 * parent-child relationship, so "which axis are they actually farther apart on" is the right
 * question, not "has any vertical overlap crept in yet" (see selectDominantAxisPorts's own doc
 * comment for the full reasoning and worked examples). 0/0 biases a plain "whichever gap is
 * bigger wins" comparison — no need to favor either axis by default. */
const CONNECTOR_AXIS_BIAS = { height: 0, width: 0 };
/** A selected connector keeps its own color (see selectedConnectorId) but renders slightly
 * thicker/larger than normal — its line, origin dot, and arrowhead are all scaled up by these
 * factors — so the selection reads as emphasis rather than a color swap. */
const CONNECTOR_SELECTED_STROKE_SCALE = 1.8;
const CONNECTOR_SELECTED_ARROW_SCALE = 1.5;
const CONNECTOR_SELECTED_DOT_SCALE = 1.6;
/** Minimum screen-px stroke width for a connector's invisible hit-zone — same idea as
 * DRAWN_LINE_HIT_ZONE_MIN_PX, for the same "thin line is hard to click at low zoom" reason. */
const CONNECTOR_HIT_ZONE_MIN_PX = 12;
/** Constant on-screen (CSS px) gap the connector's VISIBLE stroke is trimmed back from the
 * arrowhead's tip — see trimPathEndForArrow. Kept separate from the hit-zone/marker-carrier path
 * (still full-length, so the arrowhead itself still lands exactly on the target's edge and the
 * clickable area isn't shortened) purely so the line's own end (round cap included) never reads
 * as poking into or past the arrowhead's point. */
const CONNECTOR_LINE_END_GAP_PX = 3;

/**
 * Shortens a connector's routed path by `pullback` (canvas units) at its very end, so the
 * VISIBLE stroke stops short of `toPoint` instead of reaching it — used to keep a connector's
 * line (round cap included) from visually poking into its own arrowhead, which is drawn via
 * `markerEnd` on a separate, full-length carrier path (see the `connectorRoutes` rendering
 * below) so trimming this one never moves the arrowhead itself. `toSide` — the side of the
 * target rect the path's final approach direction is anchored to — gives the one axis to pull
 * back along, without needing to parse the rest of `d`: entering from the left/top means travel
 * was in the +x/+y direction, so the pulled-back point subtracts; entering from the right/bottom
 * means travel was -x/-y, so it adds. This holds exactly even for CONNECTOR_CURVED's bezier path,
 * not just a straight/elbow one — buildCurvedPortPath's own control point is offset from `toPoint`
 * purely along that same axis, so the curve's tangent at its very end lands exactly on it too.
 * Falls back to the untrimmed `d` if `toPoint`'s own coordinates don't appear verbatim at its end
 * (should never happen — both come from the same buildRectConnectorPath call — but this keeps a
 * future routing change from silently corrupting the path rather than just skipping the trim).
 */
function trimPathEndForArrow(d: string, toPoint: { x: number; y: number }, toSide: PortSide, pullback: number): string {
    const dx = toSide === 'left' ? -pullback : toSide === 'right' ? pullback : 0;
    const dy = toSide === 'top' ? -pullback : toSide === 'bottom' ? pullback : 0;
    const trimmedX = toPoint.x + dx;
    const trimmedY = toPoint.y + dy;
    const endToken = `${toPoint.x} ${toPoint.y}`;
    if (!d.endsWith(endToken)) return d;
    return `${d.slice(0, d.length - endToken.length)}${trimmedX} ${trimmedY}`;
}

/**
 * Picks black or white text for readable contrast against an arbitrary `#rrggbb` fill — used by
 * a drawn line's label rectangle, which is always filled with the line's own (user-chosen)
 * color. Standard relative-luminance threshold (~0.5 on the 0-255 per-channel weighted sum).
 * Falls back to white for anything that fails to parse as a hex color.
 */
export function getReadableTextColor(hexColor: string): string {
    const match = /^#?([0-9a-f]{6})$/i.exec(hexColor);
    if (!match) return '#ffffff';
    const hex = match[1];
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#1a1a1a' : '#ffffff';
}

/**
 * Clamps horizontal pan so the grid's left/right edges never scroll past the viewport edges —
 * and, when a frozen column is enabled (`frozenColumnGapPx` > 0, its fixed on-screen width),
 * so the canvas's own x=0 origin can never be dragged left of that reserved gap either. Without
 * this, since the frozen gap stays a constant on-screen pixel width while the grid's canvas-unit
 * equivalent of that same width GROWS the further out you zoom (frozenGapPx / zoom), zooming out
 * far enough used to let real content sitting near the canvas origin end up permanently clipped
 * under the frozen column with no way to pan it back into view — the old unconditional `0` upper
 * bound pinned the origin flush against the viewport's absolute left edge (screen x=0), which is
 * INSIDE the frozen column's own [0, frozenColumnGapPx] screen span. Bounding it at
 * `frozenColumnGapPx` instead keeps the origin's resting position flush against the frozen
 * column's own right edge, so the body's content is never dragged out from under it. When the
 * grid is narrower than the viewport's remaining (non-frozen) width, it's pinned there instead of
 * floating — the exact same "pin instead of float" behavior as before, just measured from the
 * frozen boundary rather than from screen x=0.
 */
export function clampGridPanX(panX: number, zoomVal: number, viewportWidth: number, gridWidth: number, frozenColumnGapPx = 0): number {
    const gridWidthPx = gridWidth * zoomVal;
    const maxPanX = frozenColumnGapPx;
    if (gridWidthPx <= viewportWidth - frozenColumnGapPx) return maxPanX;
    const minPanX = viewportWidth - gridWidthPx;
    return Math.min(maxPanX, Math.max(minPanX, panX));
}

/**
 * Clamps vertical pan so the grid's top/bottom edges never scroll past the viewport edges —
 * the exact vertical mirror of clampGridPanX, including the same frozen-row-aware upper bound
 * (see that function's own doc comment for why an unconditional `0` bound let content near the
 * canvas origin end up permanently hidden under the frozen row at low zoom). When the grid is
 * shorter than the viewport's remaining (non-frozen) height, it's pinned to the frozen row's own
 * bottom edge instead of floating, same as clampGridPanX pins to the frozen column's edge.
 */
export function clampGridPanY(panY: number, zoomVal: number, viewportHeight: number, gridHeight: number, frozenRowGapPx = 0): number {
    const gridHeightPx = gridHeight * zoomVal;
    const maxPanY = frozenRowGapPx;
    if (gridHeightPx <= viewportHeight - frozenRowGapPx) return maxPanY;
    const minPanY = viewportHeight - gridHeightPx;
    return Math.min(maxPanY, Math.max(minPanY, panY));
}

export function snapToGridSpacing(value: number, spacing: number): number {
    return Math.round(value / spacing) * spacing;
}

/**
 * Cuts a `[outerMin, outerMax]` span into segments at each of `cutPositions` (sorted ascending)
 * — used both for the frozen row/column's labelable segments and, implicitly, for the same
 * left-to-right/top-to-bottom ordinal a caller's own region concept (e.g. Plan's PlanRegion)
 * assigns to the matching body area. Zero cuts still returns exactly one segment spanning the
 * whole span, matching "no lines drawn yet = one region" everywhere else in the app.
 */
export function cutIntoSegments(cutPositions: number[], outerMin: number, outerMax: number): Array<{ index: number; min: number; max: number }> {
    const edges = [outerMin, ...cutPositions, outerMax];
    const segments: Array<{ index: number; min: number; max: number }> = [];
    for (let index = 0; index < edges.length - 1; index++) {
        segments.push({ index, min: edges[index], max: edges[index + 1] });
    }
    return segments;
}

const GridCanvas = forwardRef<GridCanvasHandle, GridCanvasProps>(function GridCanvas(
    {
        placements,
        renderItem,
        onBackgroundClick,
        dropForbidden,
        crosshair,
        lines = [],
        lineThickness = 4,
        selectedLineId = null,
        onLineMouseDown,
        connectors = [],
        connectorPreview = null,
        selectedConnectorId = null,
        onConnectorMouseDown,
        renderFrozenRowSegment,
        renderFrozenColumnSegment,
        overlay,
        classPrefix = 'grid-canvas',
        gridSpacing = 40,
        minorDivisions = 5,
        horizontalColumns = 16,
        visibleColumns,
        verticalExtent = DEFAULT_GRID_VERTICAL_EXTENT,
        minSnapWidth,
        minSnapHeight,
        frozenColumnGapPx = 0,
        frozenRowGapPx = 0,
        backgroundColor = '#5f84b8',
        gridStyle = 'lines',
        gridLineColor = '#ffffff',
        gridLineOpacity = 0.18,
        gridDotRadius = 1.5,
        gridDotSpacing = gridSpacing,
        dividerColor = '#ffffff',
        dividerWidth = 2,
        minZoom = 0.3,
        maxZoom = 3,
        initialZoom,
        onZoomChange,
    },
    ref,
) {
    // ── Geometry derived from props ────────────────────────────────────────────────────────
    const minorSpacing = gridSpacing / minorDivisions;
    const resolvedVisibleColumns = visibleColumns ?? Math.max(1, horizontalColumns - 1);
    const gridWidth = gridSpacing * horizontalColumns;
    const visibleWidth = gridSpacing * resolvedVisibleColumns;
    const resolvedMinSnapWidth = minSnapWidth ?? minorSpacing;
    const resolvedMinSnapHeight = minSnapHeight ?? minorSpacing;
    const hasFrozenColumn = frozenColumnGapPx > 0;
    const hasFrozenRow = frozenRowGapPx > 0;

    const svgRef = useRef<SVGSVGElement | null>(null);
    // Stable per-instance id for the scrollable-body <clipPath> — must be unique so multiple
    // GridCanvas instances on the same page don't collide over the same <clipPath> id.
    const instanceId = useRef(Math.random().toString(36).slice(2, 9)).current;
    const scrollableClipId = `grid-canvas-scrollable-clip-${instanceId}`;

    // Builds the class string for one BEM-ish suffix (or several at once, e.g. an element that's
    // both a block and a modifier) — each gets both the shared `grid-canvas-*` structural class
    // every instance relies on, and (unless classPrefix is left at its default) the per-instance
    // `${classPrefix}-*` hook class alongside it.
    const gcClass = (...suffixes: string[]): string => suffixes
        .flatMap((suffix) => classPrefix === 'grid-canvas' ? [`grid-canvas-${suffix}`] : [`grid-canvas-${suffix}`, `${classPrefix}-${suffix}`])
        .join(' ');

    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [panning, setPanning] = useState<{ startMouse: { x: number; y: number }; startPan: { x: number; y: number } } | null>(null);

    // Where zoom/pan are animating toward. The wheel handler updates these
    // instantly; a rAF loop lerps the rendered `pan`/`zoom` state toward them.
    const targetZoomRef = useRef(1);
    const targetPanRef = useRef({ x: 0, y: 0 });
    const animFrameRef = useRef<number | null>(null);
    // Mirrors pan/zoom for synchronous reads from the imperative handle (state updates are async).
    const panRef = useRef(pan);
    const zoomRef = useRef(zoom);
    useEffect(() => { panRef.current = pan; }, [pan]);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    // Mirrors the latest onBackgroundClick so the panning mouseup effect (keyed only on
    // `panning`, not on every prop change) always calls the current callback.
    const onBackgroundClickRef = useRef(onBackgroundClick);
    useEffect(() => { onBackgroundClickRef.current = onBackgroundClick; }, [onBackgroundClick]);

    useImperativeHandle(ref, () => ({
        // The frozen row/column panes render with pan.x and/or pan.y zeroed out (see the four
        // *Transform strings below) — a screen point/rect over one of them has to convert through
        // that same zeroed pan, or it resolves to a canvas point that doesn't match what's visually
        // under it.
        screenToCanvas(clientX: number, clientY: number) {
            const bounds = svgRef.current?.getBoundingClientRect();
            if (!bounds) return null;
            const z = zoomRef.current;
            const localX = clientX - bounds.left;
            const localY = clientY - bounds.top;
            const effectivePan = {
                x: localX < frozenColumnGapPx ? 0 : panRef.current.x,
                y: localY < frozenRowGapPx ? 0 : panRef.current.y,
            };
            return screenToCanvasPoint(bounds, effectivePan, z, clientX, clientY);
        },
        canvasRectToScreenRect(rect: CanvasRect) {
            const bounds = svgRef.current?.getBoundingClientRect();
            if (!bounds) return null;
            const z = zoomRef.current;
            // Which PANE a placement's data belongs to — mirrors the identical bucketing used for
            // cornerEntries/rowEntries/columnEntries/bodyEntries below, including measuring against
            // frozenColumnGapPx/frozenRowGapPx directly (a fixed zoom-of-1 reference) rather than
            // dividing by the live zoom — see that bucketing loop's own doc comment for why a
            // zoom-scaled threshold here would let a placement's computed screen rect suddenly jump
            // to a different pane's pan formula mid drag, purely from zooming, out of step with
            // which pane GridCanvas is actually rendering it in.
            const effectivePan = {
                x: rect.x + rect.width <= frozenColumnGapPx ? 0 : panRef.current.x,
                y: rect.y + rect.height <= frozenRowGapPx ? 0 : panRef.current.y,
            };
            return {
                left: bounds.left + effectivePan.x + rect.x * z,
                top: bounds.top + effectivePan.y + rect.y * z,
                width: rect.width * z,
                height: rect.height * z,
            };
        },
        getWrapperRect() {
            return svgRef.current?.getBoundingClientRect() ?? null;
        },
        snapPoint(x: number, y: number) {
            return { x: snapToGridSpacing(x, minorSpacing), y: Math.max(0, snapToGridSpacing(y, minorSpacing)) };
        },
        snapSize(width: number, height: number) {
            return {
                width: Math.max(resolvedMinSnapWidth, snapToGridSpacing(width, minorSpacing)),
                height: Math.max(resolvedMinSnapHeight, snapToGridSpacing(height, minorSpacing)),
            };
        },
        // Canvas-space (not screen px): the fixed on-screen gap divided by the current zoom.
        getFrozenColumnWidth() {
            return hasFrozenColumn ? frozenColumnGapPx / zoomRef.current : 0;
        },
        getFrozenRowHeight() {
            return hasFrozenRow ? frozenRowGapPx / zoomRef.current : 0;
        },
        getZoom() {
            return zoomRef.current;
        },
    }), [minorSpacing, resolvedMinSnapWidth, resolvedMinSnapHeight, hasFrozenColumn, hasFrozenRow, frozenColumnGapPx, frozenRowGapPx]);

    // Fit the grid's full horizontal width to the panel on mount. Deliberately
    // useLayoutEffect (not useEffect): it commits before the browser's first paint, so the
    // zoom state's default (1, from useState above) is never actually painted — with plain
    // useEffect, that wrong zoom=1 default would paint first and only jump to fitZoom a tick
    // later, a visible flash of the wrong size right after mount. (`.grid-canvas-overlay`, which
    // placed items sit in, deliberately does NOT set `will-change: transform` — see that class's
    // own doc comment in app.css for why: promoting it to its own GPU-composited layer makes the
    // browser rasterize its content ONCE and merely stretch that bitmap on every later scale
    // change instead of re-rendering it, which is exactly what made items blurry on every zoom
    // — not just the first one — while zoom is now interactively wheel-adjustable.)
    useLayoutEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        const bounds = el.getBoundingClientRect();
        if (bounds.width <= 0) return;

        // A caller-restored zoom (e.g. the project's last-used Timeline zoom) wins over the
        // computed fit-to-width level when given — still clamped to [minZoom, maxZoom] like any
        // other zoom value, in case the persisted value predates a change to those bounds.
        const computedFitZoom = Math.min(maxZoom, Math.max(minZoom, bounds.width / visibleWidth));
        const fitZoom = initialZoom != null ? Math.min(maxZoom, Math.max(minZoom, initialZoom)) : computedFitZoom;
        const initialPan = {
            x: clampGridPanX(0, fitZoom, bounds.width, gridWidth, frozenColumnGapPx),
            y: clampGridPanY(0, fitZoom, bounds.height, verticalExtent, frozenRowGapPx),
        };

        setZoom(fitZoom);
        targetZoomRef.current = fitZoom;
        setPan(initialPan);
        targetPanRef.current = initialPan;
        // Deliberately mount-only (matches the original TimelineCanvas behavior): re-running
        // this on every geometry-prop change would re-fit/re-pan out from under a user who's
        // already panned around, for the sake of props that in practice are static per instance.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reports every zoom change to the caller — the initial fit/restore above, and every step of
    // the pan/zoom lerp while wheel-zooming (runZoomAnimation, below) — not just once it settles.
    // A caller that wants to persist it (e.g. PlanPage.tsx) debounces that itself, the same way it
    // already debounces placements/lines/axisLabels; this just always has the latest value ready.
    useEffect(() => { onZoomChange?.(zoom); }, [zoom, onZoomChange]);

    const onMouseDown = (e: MouseEvent<SVGSVGElement>) => {
        // Stop any in-flight zoom-pan lerp so it doesn't fight the drag's direct pan updates.
        if (animFrameRef.current !== null) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }
        setPanning({ startMouse: { x: e.clientX, y: e.clientY }, startPan: { ...pan } });
    };

    useEffect(() => {
        if (!panning) return;

        const onMove = (e: globalThis.MouseEvent) => {
            const bounds = svgRef.current?.getBoundingClientRect();
            const rawX = panning.startPan.x + e.clientX - panning.startMouse.x;
            const rawY = panning.startPan.y + e.clientY - panning.startMouse.y;
            const nextPan = {
                x: bounds ? clampGridPanX(rawX, targetZoomRef.current, bounds.width, gridWidth, frozenColumnGapPx) : rawX,
                y: bounds ? clampGridPanY(rawY, targetZoomRef.current, bounds.height, verticalExtent, frozenRowGapPx) : rawY,
            };
            // Direct manipulation: no lerp, and the zoom target tracks along so a
            // wheel-zoom right after panning doesn't jump back toward a stale target.
            targetPanRef.current = nextPan;
            setPan(nextPan);
        };
        const onUp = (e: globalThis.MouseEvent) => {
            const dx = e.clientX - panning.startMouse.x;
            const dy = e.clientY - panning.startMouse.y;
            if (Math.hypot(dx, dy) < BACKGROUND_CLICK_THRESHOLD_PX) onBackgroundClickRef.current?.(e);
            setPanning(null);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [panning, gridWidth, verticalExtent, frozenColumnGapPx, frozenRowGapPx]);

    // Smoothly chases targetZoomRef/targetPanRef every frame until close enough to snap.
    const runZoomAnimation = () => {
        if (animFrameRef.current !== null) return;

        const step = () => {
            let reachedTarget = false;

            setZoom((currentZoom) => {
                const target = targetZoomRef.current;
                const next = currentZoom + (target - currentZoom) * LERP_FACTOR;
                if (Math.abs(target - next) < SNAP_EPSILON) {
                    reachedTarget = true;
                    return target;
                }
                return next;
            });

            setPan((currentPan) => {
                const target = targetPanRef.current;
                return {
                    x: currentPan.x + (target.x - currentPan.x) * LERP_FACTOR,
                    y: currentPan.y + (target.y - currentPan.y) * LERP_FACTOR,
                };
            });

            if (reachedTarget) {
                setPan({ ...targetPanRef.current });
                animFrameRef.current = null;
                return;
            }

            animFrameRef.current = requestAnimationFrame(step);
        };

        animFrameRef.current = requestAnimationFrame(step);
    };

    useEffect(() => () => {
        if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    }, []);

    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;

        // Plain wheel/trackpad scroll pans the canvas vertically; Ctrl/Cmd + scroll zooms
        // in/out, centered on the cursor. preventDefault also still guards against trackpad
        // pinch-zooming the page itself.
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();

            const bounds = svgRef.current?.getBoundingClientRect();
            if (!bounds) return;

            if (!e.ctrlKey && !e.metaKey) {
                // Stop any in-flight zoom-pan lerp so it doesn't fight this direct pan update.
                if (animFrameRef.current !== null) {
                    cancelAnimationFrame(animFrameRef.current);
                    animFrameRef.current = null;
                }
                const rawY = targetPanRef.current.y - e.deltaY;
                const nextPan = {
                    x: targetPanRef.current.x,
                    y: clampGridPanY(rawY, targetZoomRef.current, bounds.height, verticalExtent, frozenRowGapPx),
                };
                targetPanRef.current = nextPan;
                setPan(nextPan);
                return;
            }

            const currentZoom = targetZoomRef.current;
            const zoomFactor = Math.exp(-e.deltaY * WHEEL_ZOOM_SENSITIVITY);
            // Floored at the `minZoom` PROP, not the mount-time fit zoom — the user can zoom out
            // further than the initial view, all the way down to this absolute floor (clamped
            // panning, below, still keeps the grid from floating away once it's narrower/shorter
            // than the viewport at a low enough zoom).
            const nextZoom = Math.min(maxZoom, Math.max(minZoom, currentZoom * zoomFactor));
            if (nextZoom === currentZoom) return;

            // Keep the canvas point currently under the cursor fixed on screen: solve for the pan
            // that maps that same canvas point to the same screen position at the NEW zoom.
            const cursorX = e.clientX - bounds.left;
            const cursorY = e.clientY - bounds.top;
            const canvasX = (cursorX - targetPanRef.current.x) / currentZoom;
            const canvasY = (cursorY - targetPanRef.current.y) / currentZoom;
            const rawPan = { x: cursorX - canvasX * nextZoom, y: cursorY - canvasY * nextZoom };

            targetZoomRef.current = nextZoom;
            targetPanRef.current = {
                x: clampGridPanX(rawPan.x, nextZoom, bounds.width, gridWidth, frozenColumnGapPx),
                y: clampGridPanY(rawPan.y, nextZoom, bounds.height, verticalExtent, frozenRowGapPx),
            };
            runZoomAnimation();
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [gridWidth, verticalExtent, maxZoom, minZoom, frozenColumnGapPx, frozenRowGapPx]);

    // The wrapper's live on-screen size — tracked (not just read ad hoc) so the frozen-gap
    // segment "visible slice" math below (rowVisibleMin/Max, columnVisibleMin/Max) re-renders on
    // a plain window/panel resize too, not only when pan/zoom themselves change. Falls back to a
    // single one-shot measurement where ResizeObserver isn't available (e.g. the jsdom test
    // environment) — the visible-slice math still works from that one reading, it just won't
    // live-update on a resize no pan/zoom accompanies, in that environment only.
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        const updateSize = () => {
            const bounds = el.getBoundingClientRect();
            setViewportSize({ width: bounds.width, height: bounds.height });
        };
        updateSize();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(updateSize);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Fixed on-screen size of each frozen pane — 0 outright when that axis is disabled, otherwise
    // exactly frozenColumnGapPx/frozenRowGapPx. Never animates: every clip-path, overlay inset,
    // and divider line below reads these two variables by name, so both "enabled" and "disabled"
    // just work for all of them with no separate branching lower down.
    const frozenColumnWidthPx = hasFrozenColumn ? frozenColumnGapPx : 0;
    const frozenRowHeightPx = hasFrozenRow ? frozenRowGapPx : 0;
    // Four panes, Excel-freeze-pane style: the body scrolls on both axes, the row/column panes are
    // pinned on one axis and scroll on the other, and the corner (both frozen) never moves. Only
    // the body pane renders grid lines (see renderGridLines usage below), so only its transform is
    // needed here.
    const bodyTransform = `translate(${pan.x}, ${pan.y}) scale(${zoom})`;
    // Canvas-space equivalents of frozenColumnWidthPx/frozenRowHeightPx — what the frozen-area
    // bucketing just below (and a caller's own drop-rejection guard, via getFrozenColumnWidth/
    // getFrozenRowHeight on the imperative handle) actually compare placements against.
    const frozenColumnWidthCanvas = frozenColumnWidthPx / zoom;
    const frozenRowHeightCanvas = frozenRowHeightPx / zoom;
    // `lineThickness` is a constant ON-SCREEN (CSS px) thickness, not a canvas-unit one — dividing
    // by zoom here converts it to whatever canvas-unit value, once scaled back up by the SAME zoom
    // via the body/overlay panes' own `scale(zoom)` transform, reproduces exactly `lineThickness`
    // px on screen again. Used for both the drawn `<line>`s themselves and the frozen-gap segment
    // divider bars that mirror them (see the `lines` and `renderFrozenRowSegment`/
    // `renderFrozenColumnSegment` rendering below) — everywhere `lineThickness` is used as an
    // actual size/position inside one of those zoom-scaled panes, it's this value, not the raw prop.
    const lineThicknessCanvas = lineThickness / zoom;
    // Same "constant on-screen size" compensation as lineThicknessCanvas, for the connectors
    // rendering below.
    const connectorLineThicknessCanvas = CONNECTOR_LINE_THICKNESS_PX / zoom;
    const connectorArrowSizeCanvas = CONNECTOR_ARROW_SIZE_PX / zoom;
    const connectorOriginDotRadiusCanvas = CONNECTOR_ORIGIN_DOT_RADIUS_PX / zoom;
    // One arrowhead <marker> per distinct (color, selected) pair actually in use (see the <defs>
    // below) — an id derived from the color itself plus whether it's the enlarged selected variant
    // (and this instance's own id, same as scrollableClipId above, so multiple GridCanvas instances
    // never collide over the same <marker> id) rather than the connector's own id, since markers are
    // shared/reused across every connector of that color+selection state.
    const connectorArrowMarkerId = (color: string, selected: boolean) =>
        `grid-canvas-connector-arrow-${instanceId}-${color.replace(/[^a-zA-Z0-9]/g, '')}${selected ? '-selected' : ''}`;
    const connectorMarkerCombos = Array.from(new Map([
        ...connectors.map((c): [string, { color: string; selected: boolean }] => {
            const selected = c.id === selectedConnectorId;
            return [connectorArrowMarkerId(c.color, selected), { color: c.color, selected }];
        }),
        ...(connectorPreview ? [[connectorArrowMarkerId(connectorPreview.color, false), { color: connectorPreview.color, selected: false }] as [string, { color: string; selected: boolean }]] : []),
    ]).values());
    // Resolved once per connector (rect lookup + routing + selection state), then reused by BOTH
    // the path/arrowhead rendering below (still inside the body's normal transformed <g>, so a
    // connector's line renders underneath items like any routed connector) and the origin-dot
    // layer further down (deliberately its OWN top-level SVG, painted after every item pane —
    // same reason `.grid-canvas-dividers` is — so the dot, sitting exactly on an item's edge,
    // isn't half-covered by that item's own opaque background).
    type ResolvedConnector = { id: string; routed: NonNullable<ReturnType<typeof buildRectConnectorPath>>; isSelected: boolean };
    const connectorRoutes: ResolvedConnector[] = connectors.flatMap((connector) => {
        const fromRect = placements[connector.fromId];
        const toRect = placements[connector.toId];
        if (!fromRect || !toRect) return [];

        // CanvasRect's x/y is top-left (unlike OST's center-based node coordinates), so port
        // selection — which assumes a center-based rect — needs the placement's center, not its
        // raw x/y.
        const fromPortable = { id: connector.fromId, x: fromRect.x + fromRect.width / 2, y: fromRect.y + fromRect.height / 2, width: fromRect.width, height: fromRect.height };
        const toPortable = { id: connector.toId, x: toRect.x + toRect.width / 2, y: toRect.y + toRect.height / 2, width: toRect.width, height: toRect.height };
        const routed = buildRectConnectorPath(
            fromPortable, toPortable, connector.color,
            CONNECTOR_PORT_GAP, false, CONNECTOR_AXIS_BIAS, CONNECTOR_CURVED,
        );
        if (!routed) return [];

        return [{ id: connector.id, routed, isSelected: connector.id === selectedConnectorId }];
    });

    // Items are bucketed into the same four panes as the grid, by which frozen bounds fully
    // contain them — measured at a FIXED zoom of 1 (frozenColumnWidthPx/frozenRowHeightPx, which
    // at zoom=1 are numerically identical to their canvas-unit equivalents), NOT against the live
    // frozenColumnWidthCanvas/frozenRowHeightCanvas (= frozenColumnWidthPx/frozenRowHeightPx /
    // zoom, used below for the frozen panes' own segment/divider geometry, where zoom-scaling is
    // exactly what's wanted). Which pane an item belongs to needs to be a stable property of its
    // own geometry, not something that changes just because the user zoomed — using the live,
    // zoom-divided value here instead would let an item that's always meant to scroll normally
    // with the body (e.g. any TimelineCanvas PlanBoardCard — resolveFrozenAreaCandidate already
    // guarantees none is ever dropped fully within the frozen area) get silently reclassified
    // into the frozen row/column pane purely by zooming out far enough, since the frozen gap's
    // canvas-unit equivalent GROWS as zoom shrinks. Each pane renders with a different translate
    // (the row/corner panes never include `pan.y`; the column/corner panes never include
    // `pan.x` — see bodyTransform and the row/column/corner overlay `style` props below), so a
    // reclassified item's on-screen position would jump by a full pan.x/pan.y the instant it
    // crossed that threshold — most visible now that pan can rest at a nonzero (frozenGapPx)
    // value (see clampGridPanX/clampGridPanY's own doc comments) instead of always 0. When an
    // axis is disabled its *WidthPx/*HeightPx is 0, so "placement.x + width <= 0" is never true
    // for a real (non-degenerate) placement — items simply never bucket into that axis's pane,
    // with no separate disabled-axis branch needed here.
    const cornerEntries: [string, CanvasRect][] = [];
    const rowEntries: [string, CanvasRect][] = [];
    const columnEntries: [string, CanvasRect][] = [];
    const bodyEntries: [string, CanvasRect][] = [];
    for (const entry of Object.entries(placements)) {
        const [, placement] = entry;
        const inFrozenColumn = placement.x + placement.width <= frozenColumnWidthPx;
        const inFrozenRow = placement.y + placement.height <= frozenRowHeightPx;
        if (inFrozenColumn && inFrozenRow) cornerEntries.push(entry);
        else if (inFrozenRow) rowEntries.push(entry);
        else if (inFrozenColumn) columnEntries.push(entry);
        else bodyEntries.push(entry);
    }

    // Minor layer drops out below MINOR_GRID_HIDE_ZOOM_THRESHOLD (see that constant's doc
    // comment) — reusing the lerped `zoom` state so it fades in/out in step with the same
    // animation everything else pans/zooms with, rather than snapping on a raw wheel event.
    const showMinorGrid = zoom >= MINOR_GRID_HIDE_ZOOM_THRESHOLD;

    // 'dots' renders a single flat layer at its own independent spacing (matching the OST
    // canvas's dot grid exactly — no minor/major split); 'lines' keeps the existing two-layer
    // cross-hatch (minor cells fading out below MINOR_GRID_HIDE_ZOOM_THRESHOLD, major always on).
    const renderGridLines = (xMin: number, xMax: number, yMin: number, yMax: number) =>
        gridStyle === 'dots' ? (
            <GridLines
                spacing={gridDotSpacing}
                color={gridLineColor}
                opacity={gridLineOpacity}
                variant='dots'
                dotRadius={gridDotRadius}
                xMin={xMin}
                xMax={xMax}
                yMin={yMin}
                yMax={yMax}
            />
        ) : (
            <>
                {showMinorGrid && (
                    <GridLines
                        spacing={minorSpacing}
                        color={gridLineColor}
                        opacity={gridLineOpacity}
                        variant={gridStyle}
                        xMin={xMin}
                        xMax={xMax}
                        yMin={yMin}
                        yMax={yMax}
                    />
                )}
                <GridLines
                    spacing={gridSpacing}
                    color={gridLineColor}
                    opacity={gridLineOpacity}
                    variant={gridStyle}
                    xMin={xMin}
                    xMax={xMax}
                    yMin={yMin}
                    yMax={yMax}
                />
            </>
        );

    const renderPaneItems = (entries: [string, CanvasRect][]) => entries.map(([id, placement]) => renderItem(id, placement));

    // The same line positions that cut the body into regions (see PlanRegion, types.ts) also cut
    // the frozen row/column's gap into labelable segments — sorted so segment `index` is a stable
    // left-to-right (row)/top-to-bottom (column) ordinal regardless of the order lines were drawn in.
    const verticalLinePositions = lines.filter((line) => line.orientation === 'vertical').map((line) => line.position).sort((a, b) => a - b);
    const horizontalLinePositions = lines.filter((line) => line.orientation === 'horizontal').map((line) => line.position).sort((a, b) => a - b);
    const rowSegments = hasFrozenRow && renderFrozenRowSegment ? cutIntoSegments(verticalLinePositions, 0, gridWidth) : [];
    const columnSegments = hasFrozenColumn && renderFrozenColumnSegment ? cutIntoSegments(horizontalLinePositions, 0, verticalExtent) : [];
    // The portion of each frozen-gap segment that's actually on screen right now, in the SAME
    // canvas-unit coordinate space as the segment's own min/max — i.e. `[frozenColumnWidthPx,
    // viewportSize.width]` (the row pane's own visible screen-x range) converted back through the
    // current pan/zoom. Passed to renderFrozenRowSegment/renderFrozenColumnSegment so a caller can
    // keep its own label content centered in whatever's actually visible (see PlanAxisLabelCell)
    // instead of centered in the segment's full extent, which can run off screen while zoomed in.
    const rowVisibleMin = (frozenColumnWidthPx - pan.x) / zoom;
    const rowVisibleMax = (viewportSize.width - pan.x) / zoom;
    const columnVisibleMin = (frozenRowHeightPx - pan.y) / zoom;
    const columnVisibleMax = (viewportSize.height - pan.y) / zoom;

    return (
        <div
            className={gcClass('wrapper')}
            style={{ '--grid-canvas-accent-color': backgroundColor, '--grid-canvas-line-color': dividerColor } as CSSProperties}
        >
            <svg
                ref={svgRef}
                className={[
                    gcClass('svg'),
                    dropForbidden ? gcClass('svg--drop-forbidden') : '',
                    crosshair ? gcClass('svg--crosshair') : '',
                ].filter(Boolean).join(' ')}
                style={{ background: backgroundColor }}
                onMouseDown={onMouseDown}
            >
                <defs>
                    {/* Body: scrolls on both axes — clipped to stay clear of the frozen row and column.
                        The frozen row/column/corner panes intentionally have no grid lines of their own
                        (and so no clipPaths here), keeping the grid visible only in the scrollable body. */}
                    <clipPath id={scrollableClipId}>
                        <rect x={frozenColumnWidthPx} y={frozenRowHeightPx} width='100%' height='100%' />
                    </clipPath>
                    {/* One arrowhead per distinct (color, selected) combo actually in use — see the
                        `connectors` rendering below. markerUnits='userSpaceOnUse' + a canvas-unit
                        markerWidth/Height (already zoom-divided, like lineThicknessCanvas) keeps the
                        arrow a constant on-screen size regardless of zoom, same as the drawn lines
                        above; orient='auto' aligns it to the path's own tangent at its end, so no
                        manual angle math is needed. The selected variant is simply scaled up by
                        CONNECTOR_SELECTED_ARROW_SCALE, same color as normal — see that constant's
                        doc comment. */}
                    {connectorMarkerCombos.map(({ color, selected }) => (
                        <marker
                            key={connectorArrowMarkerId(color, selected)}
                            id={connectorArrowMarkerId(color, selected)}
                            viewBox='0 0 10 10'
                            refX={8.5}
                            refY={5}
                            markerWidth={connectorArrowSizeCanvas * (selected ? CONNECTOR_SELECTED_ARROW_SCALE : 1)}
                            markerHeight={connectorArrowSizeCanvas * (selected ? CONNECTOR_SELECTED_ARROW_SCALE : 1)}
                            markerUnits='userSpaceOnUse'
                            orient='auto'
                        >
                            <path d='M 0 0 L 10 5 L 0 10 Z' fill={color} />
                        </marker>
                    ))}
                </defs>

                <g clipPath={`url(#${scrollableClipId})`}>
                    <g transform={bodyTransform}>
                        {/* Grid lines start at the body's true (0,0) origin and, like the user-drawn lines
                            just below, run out to INFINITE_EXTENT_CANVAS_UNITS on the right and bottom
                            rather than stopping at gridWidth/verticalExtent — so panning/zooming out never
                            reveals a visible edge where the grid pattern just ends. This used to start at
                            frozenColumnWidthCanvas/frozenRowHeightCanvas instead of 0 — an extra margin of
                            safety meant to keep the pattern from ever being drawn (and so, even
                            hypothetically, visible) behind the frozen row/column — but frozenColumnWidthCanvas
                            (= frozenColumnGapPx / zoom) GROWS as zoom shrinks, and once clampGridPanX/
                            clampGridPanY started pinning canvas x=0/y=0 flush against the frozen boundary
                            (rather than letting it scroll further right/down, see those functions' own doc
                            comments), that growing offset started excluding real ON-SCREEN canvas area from
                            the pattern — the grid going missing along the left/top edges the further out you
                            zoomed. The clip on the outer <g> already fully excludes anything actually behind
                            the frozen row/column on its own; starting at 0 here just lets the pattern cover
                            all of what that clip lets through. */}
                        {renderGridLines(0, INFINITE_EXTENT_CANVAS_UNITS, 0, INFINITE_EXTENT_CANVAS_UNITS)}
                        {/* User-drawn lines: rendered in this same clipped/transformed group as the grid
                            lines above, so they're confined to the scrollable body — never the frozen
                            row/column — no matter where `position` falls. Each starts at the body's near
                            edge (left for a horizontal line, top for a vertical one) and runs to
                            INFINITE_EXTENT_CANVAS_UNITS on its far end (right/bottom respectively) — well
                            past the grid's own finite gridWidth/verticalExtent — so it keeps going off
                            the visible edge instead of visibly terminating once zoomed out enough to see
                            the grid's actual bounds; see that constant's own doc comment. */}
                        {lines.map((line) => {
                            const isVertical = line.orientation === 'vertical';
                            const x1 = isVertical ? line.position : 0;
                            const x2 = isVertical ? line.position : INFINITE_EXTENT_CANVAS_UNITS;
                            const y1 = isVertical ? 0 : line.position;
                            const y2 = isVertical ? INFINITE_EXTENT_CANVAS_UNITS : line.position;
                            const selected = line.id === selectedLineId;
                            const handleMouseDown = (e: MouseEvent<SVGLineElement>) => {
                                e.stopPropagation();
                                onLineMouseDown?.(line.id, e);
                            };
                            return (
                                <g key={line.id}>
                                    {selected && (
                                        <line
                                            x1={x1} x2={x2} y1={y1} y2={y2}
                                            stroke={dividerColor}
                                            strokeWidth={lineThicknessCanvas * 2.5}
                                            strokeOpacity={0.35}
                                            pointerEvents='none'
                                        />
                                    )}
                                    <line
                                        x1={x1} x2={x2} y1={y1} y2={y2}
                                        stroke={line.color}
                                        strokeWidth={lineThicknessCanvas}
                                        style={{ cursor: onLineMouseDown ? 'pointer' : undefined }}
                                        onMouseDown={handleMouseDown}
                                    />
                                    {/* Invisible wider stroke sharing the same handler — at low zoom
                                        the visible line above can render thinner on screen than is
                                        comfortably clickable/draggable; this keeps the hit target usable
                                        without visually thickening the line itself. Both terms here are
                                        already the same "canvas units that reproduce a constant screen
                                        px size once scaled by zoom" quantity, so the max is a fair
                                        screen-px comparison. */}
                                    <line
                                        x1={x1} x2={x2} y1={y1} y2={y2}
                                        stroke='transparent'
                                        strokeWidth={Math.max(lineThicknessCanvas, DRAWN_LINE_HIT_ZONE_MIN_PX / zoom)}
                                        style={{ cursor: onLineMouseDown ? 'pointer' : undefined }}
                                        onMouseDown={handleMouseDown}
                                    />
                                </g>
                            );
                        })}
                        {/* Routed connectors between placed items (see CanvasConnector above) —
                            resolved (connectorRoutes) against this same `placements` prop, rendered
                            after `lines` so they sit visually on top of both the grid and any drawn
                            lines, but stay confined to the same clipped/transformed body group. Each
                            connector's own origin dot (and the live drag preview's line/dot) is
                            deliberately NOT rendered here — see the top-level
                            `.grid-canvas-connector-overlay` SVG further down, painted after every
                            item pane so those read above cards instead of underneath them. */}
                        {connectorRoutes.map(({ id, routed, isSelected }) => {
                            const handleMouseDown = (e: MouseEvent<SVGPathElement>) => {
                                e.stopPropagation();
                                onConnectorMouseDown?.(id, e);
                            };
                            const strokeWidthCanvas = connectorLineThicknessCanvas * (isSelected ? CONNECTOR_SELECTED_STROKE_SCALE : 1);
                            // Pulls the visible stroke back far enough to clear its own round cap
                            // (half its width) plus a small constant gap — see
                            // CONNECTOR_LINE_END_GAP_PX/trimPathEndForArrow's doc comments.
                            const pullback = strokeWidthCanvas / 2 + CONNECTOR_LINE_END_GAP_PX / zoom;
                            const visibleD = trimPathEndForArrow(routed.d, routed.toPoint, routed.toSide, pullback);

                            return (
                                <g key={id}>
                                    <path
                                        d={visibleD}
                                        fill='none'
                                        stroke={routed.color}
                                        strokeWidth={strokeWidthCanvas}
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        pointerEvents='none'
                                    />
                                    {/* Invisible wider stroke sharing the same handler — makes a thin
                                        connector comfortably clickable, same idea as the drawn lines'
                                        own hit-zone above. Carries the arrowhead marker itself (rather
                                        than the visible stroke above) since it's the untrimmed
                                        full-length path — so the arrowhead still lands exactly on the
                                        target's edge regardless of how far the visible stroke is
                                        pulled back from it. */}
                                    <path
                                        d={routed.d}
                                        fill='none'
                                        stroke='transparent'
                                        strokeWidth={Math.max(connectorLineThicknessCanvas, CONNECTOR_HIT_ZONE_MIN_PX / zoom)}
                                        markerEnd={`url(#${connectorArrowMarkerId(routed.color, isSelected)})`}
                                        style={{ cursor: onConnectorMouseDown ? 'pointer' : undefined }}
                                        onMouseDown={handleMouseDown}
                                    />
                                </g>
                            );
                        })}
                    </g>
                </g>
            </svg>

            {/* HTML overlay for placed items — shares the SVG group's exact pan/zoom transform.
                --zoom lets a placed item's own styling cancel out this ambient scale for
                properties that should stay a constant on-screen size, like a border-radius.

                Each pane below is two nested elements, not one, mirroring the SVG grid's
                clipPath-on-an-untransformed-<g> structure above: an outer .grid-canvas-overlay-clip
                that carries the clip-path and is NEVER itself panned/scaled, wrapping an inner
                .grid-canvas-overlay that carries the translate/scale and the actual items. Clipping
                and panning used to live on the same element; since CSS clip-path is evaluated in that
                element's own pre-transform coordinate space and translate is applied to the whole
                clipped result afterward, the clip boundary panned right along with the content instead
                of staying pinned to the frozen row/column's fixed screen position — letting an item
                dragged past the boundary visibly slide over/behind the frozen pane instead of
                disappearing under it. Splitting the two elements fixes the clip in screen space
                (frozenColumnWidthPx/frozenRowHeightPx below are already the fixed on-screen sizes)
                while pan/scale keep animating just the inner element, same as the SVG. */}
            <div
                className={gcClass('overlay-clip')}
                style={{ clipPath: `inset(${frozenRowHeightPx}px 0 0 ${frozenColumnWidthPx}px)` }}
            >
                <div
                    className={gcClass('overlay')}
                    // Deliberately NOT the `transform` shorthand: React stops applying updates to
                    // it on this element after the first render (repro'd via MutationObserver —
                    // zero style mutations on subsequent pans, even though the SVG <g>'s `transform`
                    // attribute above, built from the exact same pan/zoom values, updates correctly
                    // every time). The standalone `translate`/`scale` longhands don't hit whatever
                    // this is and update reliably every render — `scale` has to be a string, since
                    // React appends "px" to bare numbers for properties outside its unitless
                    // allow-list, which silently made the browser reject the value.
                    style={{ translate: `${pan.x}px ${pan.y}px`, scale: String(zoom), transformOrigin: '0 0', '--zoom': zoom } as CSSProperties}
                >
                    {renderPaneItems(bodyEntries)}
                </div>
            </div>

            {/* Frozen-row overlay — pinned vertically, scrolls horizontally with the rest of the grid.
                Also hosts the row's name-label segments (renderFrozenRowSegment), each positioned/sized
                exactly like a placed item so it participates in the same pan/scale transform. */}
            {hasFrozenRow && (
                <div className={gcClass('overlay-clip')} style={{ clipPath: `inset(0 0 0 ${frozenColumnWidthPx}px)` }}>
                    <div
                        className={gcClass('overlay')}
                        style={{ translate: `${pan.x}px 0px`, scale: String(zoom), transformOrigin: '0 0', '--zoom': zoom } as CSSProperties}
                    >
                        {renderPaneItems(rowEntries)}
                        {renderFrozenRowSegment && rowSegments.map(({ index, min, max }) => (
                            <div
                                key={`row-segment-${index}`}
                                className={gcClass('frozen-row-segment')}
                                style={{ position: 'absolute', left: min, top: 0, width: max - min, height: frozenRowHeightCanvas }}
                            >
                                {renderFrozenRowSegment(index, min, max, Math.max(min, rowVisibleMin), Math.min(max, rowVisibleMax))}
                            </div>
                        ))}
                        {/* Segment dividers, one per vertical line — a dedicated bar CENTERED on the
                            line's own position, rather than a border on the segment div next to it: a
                            CSS border is drawn INSET from its box's edge (occupying [edge-thickness,
                            edge]), while the actual drawn <line> below has its stroke CENTERED on
                            `position` (occupying [position-thickness/2, position+thickness/2] per SVG's
                            default stroke alignment) — those two conventions disagree by half the
                            thickness, which is exactly what made the border-based divider visibly drift
                            off the real line once thickness was more than a sliver. Matching the line's
                            own centered-stroke geometry exactly (same position, same thickness, same
                            color) is what keeps this pixel-aligned with it at any zoom, rather than a
                            border that merely happened to be positioned close by. */}
                        {renderFrozenRowSegment && verticalLinePositions.map((position, i) => (
                            <div
                                key={`row-divider-${i}`}
                                className={gcClass('frozen-row-divider')}
                                style={{
                                    position: 'absolute', top: 0, left: position - lineThicknessCanvas / 2,
                                    width: lineThicknessCanvas, height: frozenRowHeightCanvas, background: dividerColor,
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Frozen-column overlay — pinned horizontally, scrolls vertically with the rest of the grid.
                Also hosts the column's name-label segments (renderFrozenColumnSegment). */}
            {hasFrozenColumn && (
                <div className={gcClass('overlay-clip')} style={{ clipPath: `inset(${frozenRowHeightPx}px 0 0 0)` }}>
                    <div
                        className={gcClass('overlay')}
                        style={{ translate: `0px ${pan.y}px`, scale: String(zoom), transformOrigin: '0 0', '--zoom': zoom } as CSSProperties}
                    >
                        {renderPaneItems(columnEntries)}
                        {renderFrozenColumnSegment && columnSegments.map(({ index, min, max }) => (
                            <div
                                key={`column-segment-${index}`}
                                className={gcClass('frozen-column-segment')}
                                style={{ position: 'absolute', left: 0, top: min, width: frozenColumnWidthCanvas, height: max - min }}
                            >
                                {renderFrozenColumnSegment(index, min, max, Math.max(min, columnVisibleMin), Math.min(max, columnVisibleMax))}
                            </div>
                        ))}
                        {/* Same centered-bar divider treatment as the row segments above (see that
                            block's comment for why), one per horizontal line, along the bottom edge. */}
                        {renderFrozenColumnSegment && horizontalLinePositions.map((position, i) => (
                            <div
                                key={`column-divider-${i}`}
                                className={gcClass('frozen-column-divider')}
                                style={{
                                    position: 'absolute', left: 0, top: position - lineThicknessCanvas / 2,
                                    width: frozenColumnWidthCanvas, height: lineThicknessCanvas, background: dividerColor,
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Corner overlay — frozen on both axes, never moves. Only rendered when BOTH axes are
                frozen (a lone frozen row or column has no corner to speak of — its items are
                already fully covered by the row/column pane above). Explicitly sized to its own
                fixed width/height (not the shared .grid-canvas-overlay-clip's inset:0-fills-parent
                pattern) so an item bucketed here is clipped away exactly like the corner's grid
                lines above. */}
            {hasFrozenColumn && hasFrozenRow && (
                <div
                    className={gcClass('overlay-clip')}
                    style={{ top: 0, left: 0, width: frozenColumnWidthPx, height: frozenRowHeightPx }}
                >
                    <div
                        className={gcClass('overlay')}
                        style={{ translate: '0px 0px', scale: String(zoom), transformOrigin: '0 0', '--zoom': zoom } as CSSProperties}
                    >
                        {renderPaneItems(cornerEntries)}
                    </div>
                </div>
            )}

            {/* Dividers marking the frozen row/column boundaries — fixed in screen space, and
                deliberately its own SVG painted after every item pane above (rather than living
                inside the base grid <svg>, which is the very first thing in this wrapper) so the
                boundary always reads on top of an item sitting flush against it instead of being
                partially covered by it. pointer-events: none keeps it purely visual — clicks and
                drags still reach the items and grid underneath. */}
            {(hasFrozenColumn || hasFrozenRow) && (
                <svg className={gcClass('dividers')}>
                    {hasFrozenColumn && (
                        <line x1={frozenColumnWidthPx} x2={frozenColumnWidthPx} y1={0} y2='100%' stroke={dividerColor} strokeWidth={dividerWidth} />
                    )}
                    {hasFrozenRow && (
                        <line x1={0} x2='100%' y1={frozenRowHeightPx} y2={frozenRowHeightPx} stroke={dividerColor} strokeWidth={dividerWidth} />
                    )}
                </svg>
            )}

            {/* Every connector's origin dot, PLUS the live drag-in-progress preview line/arrowhead
                (not a committed connector's own path — that still routes underneath items, same as
                before) — deliberately its own SVG painted after every item pane (same reason
                .grid-canvas-dividers, just above, is), so a dot sitting exactly on an item's edge
                reads as a full circle above that item instead of being half-covered by its opaque
                background, and the preview line the user is actively dragging reads on top of
                every card it crosses instead of disappearing underneath them mid-drag. Shares the
                body's own clip/transform (scrollableClipId, bodyTransform) so this stays confined
                to the scrollable body and tracks pan/zoom exactly like the connectors it belongs
                to. Purely visual: pointer-events: none lets clicks still reach a connector's own
                path (or an item) underneath it. */}
            {(connectorRoutes.length > 0 || connectorPreview) && (
                <svg className={gcClass('connector-overlay')}>
                    <g clipPath={`url(#${scrollableClipId})`}>
                        <g transform={bodyTransform}>
                            {connectorRoutes.map(({ id, routed, isSelected }) => (
                                <circle
                                    key={id}
                                    cx={routed.fromPoint.x}
                                    cy={routed.fromPoint.y}
                                    r={connectorOriginDotRadiusCanvas * (isSelected ? CONNECTOR_SELECTED_DOT_SCALE : 1)}
                                    fill={routed.color}
                                />
                            ))}
                            {connectorPreview && (() => {
                                // Same visible-stroke pullback as a committed connector's path above,
                                // but computed along the preview's own (possibly diagonal, since it
                                // isn't routed) direction rather than via toSide — there's no fixed
                                // axis to rely on while the user is still dragging freely.
                                const dx = connectorPreview.to.x - connectorPreview.from.x;
                                const dy = connectorPreview.to.y - connectorPreview.from.y;
                                const dist = Math.hypot(dx, dy) || 1;
                                const pullback = connectorLineThicknessCanvas / 2 + CONNECTOR_LINE_END_GAP_PX / zoom;
                                const trimmedX = connectorPreview.to.x - (dx / dist) * pullback;
                                const trimmedY = connectorPreview.to.y - (dy / dist) * pullback;
                                return (
                                    <>
                                        <line
                                            x1={connectorPreview.from.x} y1={connectorPreview.from.y}
                                            x2={trimmedX} y2={trimmedY}
                                            stroke={connectorPreview.color}
                                            strokeWidth={connectorLineThicknessCanvas}
                                            strokeLinecap='round'
                                        />
                                        {/* Untrimmed, invisible carrier for the arrowhead marker — same
                                            reason as a committed connector's hit-zone path above: keeps
                                            the arrowhead exactly at the drag's current target point. */}
                                        <line
                                            x1={connectorPreview.from.x} y1={connectorPreview.from.y}
                                            x2={connectorPreview.to.x} y2={connectorPreview.to.y}
                                            stroke='transparent'
                                            strokeWidth={connectorLineThicknessCanvas}
                                            markerEnd={`url(#${connectorArrowMarkerId(connectorPreview.color, false)})`}
                                        />
                                        <circle cx={connectorPreview.from.x} cy={connectorPreview.from.y} r={connectorOriginDotRadiusCanvas} fill={connectorPreview.color} />
                                    </>
                                );
                            })()}
                        </g>
                    </g>
                </svg>
            )}

            {/* Labels for named drawn lines — above/left of a horizontal line, left of a vertical
                one, filled with the line's own color and joined flush to it (see DrawnLine's own
                doc comment). Rendered as a plain HTML overlay, not inside the SVG body group:
                each label mixes ONE axis pinned to the pannable body's near edge (so panning the
                line along its own length — the only kind of pan that could otherwise carry an
                edge-to-edge line's label off screen — never does) with the OTHER axis tracking
                the exact same pan/zoom math the line itself renders with (so it stays visually
                joined to the line's actual position). Mixing a pinned axis with a tracked one is
                far simpler as directly computed inline left/top than an SVG/CSS transform would
                allow, hence HTML here rather than another <line>-adjacent SVG element. */}
            {lines.filter((line) => line.name).map((line) => {
                const isVertical = line.orientation === 'vertical';
                const trackedPx = isVertical
                    ? pan.x + line.position * zoom
                    : pan.y + line.position * zoom;
                const pinnedPx = isVertical
                    ? frozenRowHeightPx + DRAWN_LINE_LABEL_EDGE_GAP_PX
                    : frozenColumnWidthPx + DRAWN_LINE_LABEL_EDGE_GAP_PX;
                return (
                    <div
                        key={line.id}
                        className={gcClass('line-label')}
                        style={{
                            left: isVertical ? trackedPx : pinnedPx,
                            top: isVertical ? pinnedPx : trackedPx,
                            // Anchors the rect's own trailing edge (right for a vertical line's
                            // left-of-line label, bottom for a horizontal line's above-line one)
                            // exactly on the tracked coordinate — touching the line, not floating
                            // off of it.
                            transform: isVertical ? 'translateX(-100%)' : 'translateY(-100%)',
                            background: line.color,
                            color: getReadableTextColor(line.color),
                        }}
                    >
                        {line.name}
                    </div>
                );
            })}

            {overlay}
        </div>
    );
});

export default GridCanvas;
